import { useState } from 'react'
import type { ButtonHTMLAttributes, CSSProperties } from 'react'

/* ============================================================================
   家用光世代路由器管理後台 — Design System
   視覺方向：apple.com 高級產品質感（極致留白、克制色彩、精準字級、藥丸按鈕）

   色彩規範（極度克制，僅限下列色票，禁止引入清單外顏色、禁止漸層、禁止第二強調色）
   字體：標題字重 600 / letter-spacing -0.02em；資料類（MAC/IP/序號）用等寬字體
   密度規則：低密度頁（單一任務）vs 高密度頁（列表/表格），見 tokens 內 density 分級
   ============================================================================ */

export const routerTokens = {
  color: {
    textPrimary: '#1D1D1F', // 主文字
    textSecondary: '#86868B', // 次要文字／輔助說明
    border: '#D2D2D7', // 分隔線／邊框（僅用於表單欄位本身）
    bgMuted: '#F5F5F7', // 淺灰區塊背景（分區用，取代邊框）
    bgCard: '#FFFFFF', // 純白卡片背景
    accent: '#0066CC', // 唯一強調色（連結、可互動元素）
    buttonPrimary: '#1D1D1F', // 主要按鈕（黑底）
    buttonPrimaryText: '#FFFFFF',
    danger: '#D70015', // 危險／高風險操作專用色（只允許模式、清除設定等）
    success: '#1DB954', // 成功／在線狀態（只用於狀態小圓點與強度指示，不做大面積填色）
  },
  font: {
    family: '-apple-system, "SF Pro Display", "PingFang TC", "Noto Sans TC", sans-serif',
    mono: '"SF Mono", "IBM Plex Mono", monospace',
  },
  // 字級層級：層級數量要少，靠「級距大」製造層次，不靠加粗
  type: {
    pageTitle: { fontSize: 34, fontWeight: 600, letterSpacing: '-0.02em' }, // 32-38px / 600
    cardTitle: { fontSize: 18, fontWeight: 500 }, // 17-20px / 500
    body: { fontSize: 15, fontWeight: 400 }, // 15-16px / 400
    caption: { fontSize: 12.5, fontWeight: 400, color: '#86868B' }, // 12-13px / 400，一律 #86868B
    data: { fontSize: 14, fontWeight: 400 }, // 資料類（MAC/IP/序號），等寬字體
  },
  radius: {
    pill: 999, // 按鈕一律全圓角
    cardLowDensity: 20, // 單任務頁卡片 20-24px
    cardHighDensity: 14, // 高密度頁卡片 14-16px
    input: 12, // 表單欄位
  },
  shadow: {
    subtle: '0 1px 2px rgba(0,0,0,0.04)', // 極輕微層次，不可作為主要視覺語言
  },
  spacing: {
    lowDensitySection: 48, // 段落間距 40-56px
    highDensitySection: 20, // 段落間距收斂 16-24px
  },
  // 密度分級：不同頁面調整視覺份量，但色彩／字體／按鈕形狀／圓角邏輯維持一致
  density: {
    low: {
      pageTitle: 34, // 大標題
      sectionGap: 48,
      formMaxWidth: 440, // 表單欄位寬度限制 400-480px 並置中，不要滿版
      cardRadius: 20,
    },
    high: {
      pageTitle: 24, // 標題縮小到 22-26px
      sectionGap: 20,
      formMaxWidth: '100%', // 列表／欄位可滿版
      cardRadius: 14,
    },
  },
}

const t = routerTokens

/* ----------------------------------------------------------------------------
   輔助：藥丸按鈕（圓角 999px）
   variant:
   - primary   黑底白字（主要動作）
   - secondary 白底外框（僅用於「需要並列選擇」的次要按鈕）
   - textLink  文字連結 + chevron（次要動作的優先選擇，例如「了解更多 ›」）
   - danger    高風險操作專用（#D70015），只用於真正高風險的操作
   ---------------------------------------------------------------------------- */

export type ButtonVariant = 'primary' | 'secondary' | 'textLink' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  chevron?: boolean
}

const buttonVariantStyle: Record<ButtonVariant, CSSProperties> = {
  primary: {
    backgroundColor: t.color.buttonPrimary,
    color: t.color.buttonPrimaryText,
  },
  secondary: {
    backgroundColor: t.color.bgCard,
    color: t.color.textPrimary,
    border: `1px solid ${t.color.border}`,
  },
  textLink: {
    backgroundColor: 'transparent',
    color: t.color.accent,
    padding: 0,
    height: 'auto',
  },
  danger: {
    backgroundColor: t.color.danger,
    color: '#FFFFFF',
  },
}

export function Button({
  variant = 'primary',
  chevron = false,
  children,
  disabled,
  style,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onFocus,
  onBlur,
  ...rest
}: ButtonProps) {
  // 互動狀態：default / hover / active(pressed) / focus / disabled，逐狀態有不同視覺
  const [hovered, setHovered] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [focused, setFocused] = useState(false)

  const base: CSSProperties = {
    fontFamily: t.font.family,
    fontSize: 15,
    fontWeight: 500,
    borderRadius: t.radius.pill,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: variant === 'textLink' ? 'auto' : 44,
    padding: variant === 'textLink' ? 0 : '0 22px',
    border: variant === 'secondary' ? `1px solid ${t.color.border}` : 'none',
    whiteSpace: 'nowrap',
    opacity: disabled ? 0.5 : 1,
    transition:
      'opacity 0.15s ease, background-color 0.15s ease, transform 0.06s ease, box-shadow 0.15s ease',
    ...style,
  }

  // 各狀態疊加（disabled 時維持 50% 透明度，不套用互動效果）
  const state: CSSProperties = {}
  if (!disabled) {
    if (hovered) {
      if (variant === 'primary') state.backgroundColor = 'rgba(29, 29, 31, 0.84)'
      else if (variant === 'danger') state.backgroundColor = 'rgba(215, 0, 21, 0.88)'
      else if (variant === 'secondary') state.backgroundColor = t.color.bgMuted
      else if (variant === 'textLink') {
        state.textDecoration = 'underline'
        state.textUnderlineOffset = 3
      }
    }
    if (pressed) {
      state.transform = 'scale(0.98)'
      if (variant === 'primary') state.backgroundColor = '#000000'
      else if (variant === 'danger') state.backgroundColor = '#B00013'
      else if (variant === 'secondary') state.backgroundColor = 'rgba(29, 29, 31, 0.08)'
    }
    if (focused) {
      const ringColor = variant === 'danger' ? 'rgba(215, 0, 21, 0.28)' : 'rgba(0, 102, 204, 0.28)'
      state.boxShadow = `0 0 0 3px ${ringColor}`
      if (variant === 'textLink') state.borderRadius = 4
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onMouseEnter={(e) => {
        setHovered(true)
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHovered(false)
        setPressed(false)
        onMouseLeave?.(e)
      }}
      onMouseDown={(e) => {
        setPressed(true)
        onMouseDown?.(e)
      }}
      onMouseUp={(e) => {
        setPressed(false)
        onMouseUp?.(e)
      }}
      onFocus={(e) => {
        setFocused(true)
        onFocus?.(e)
      }}
      onBlur={(e) => {
        setFocused(false)
        setHovered(false)
        setPressed(false)
        onBlur?.(e)
      }}
      style={{ ...base, ...buttonVariantStyle[variant], ...state }}
      {...rest}
    >
      {children}
      {chevron && variant === 'textLink' && <ChevronRightIcon size={14} color={t.color.accent} />}
    </button>
  )
}

/* ----------------------------------------------------------------------------
   表單輸入框
   - 圓角 12px、1px 邊框 #D2D2D7、高度 46px、focus 時邊框變強調色
   - mono 模式：資料類輸入（MAC / IP）使用等寬字體
   - maxWidth 預設 440px（低密度頁置中表單），高密度頁可傳 '100%'
   ---------------------------------------------------------------------------- */

export interface TextInputProps {
  label?: string
  helper?: string
  error?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: string
  mono?: boolean
  maxWidth?: number | string
  autoComplete?: string
}

export function TextInput({
  label,
  helper,
  error,
  value,
  onChange,
  placeholder,
  type = 'text',
  mono = false,
  maxWidth = 440,
  autoComplete,
}: TextInputProps) {
  const [focused, setFocused] = useState(false)

  const borderColor = error ? t.color.danger : focused ? t.color.accent : t.color.border

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxWidth,
        width: '100%',
      }}
    >
      {label && (
        <label
          style={{
            fontFamily: t.font.family,
            fontSize: 13,
            fontWeight: 500,
            color: t.color.textPrimary,
          }}
        >
          {label}
        </label>
      )}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          fontFamily: mono ? t.font.mono : t.font.family,
          fontSize: mono ? 14 : 16,
          color: t.color.textPrimary,
          height: 46,
          padding: '0 14px',
          borderRadius: t.radius.input,
          border: `1px solid ${borderColor}`,
          outline: 'none',
          backgroundColor: t.color.bgCard,
          width: '100%',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease',
        }}
      />
      {error && (
        <span style={{ fontFamily: t.font.family, fontSize: 12.5, color: t.color.danger }}>
          {error}
        </span>
      )}
      {!error && helper && (
        <span
          style={{
            fontFamily: t.font.family,
            fontSize: 12.5,
            color: t.color.textSecondary,
          }}
        >
          {helper}
        </span>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------------------
   分段控制器（Segmented Control）
   三選一／多選一的模式切換，優先使用此元件而非卡片並排
   ---------------------------------------------------------------------------- */

export interface SegmentOption<T extends string = string> {
  value: T
  label: string
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'md' | 'sm'
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
}: SegmentedControlProps<T>) {
  const height = size === 'md' ? 40 : 32

  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        backgroundColor: t.color.bgMuted,
        borderRadius: t.radius.pill,
        padding: 3,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            style={{
              fontFamily: t.font.family,
              fontSize: 14,
              fontWeight: active ? 500 : 400,
              color: active ? t.color.textPrimary : t.color.textSecondary,
              backgroundColor: active ? t.color.bgCard : 'transparent',
              borderRadius: t.radius.pill,
              height,
              padding: '0 18px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: active ? t.shadow.subtle : 'none',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/* ----------------------------------------------------------------------------
   狀態徽章（狀態小圓點 + 文字）
   成功／在線：#1DB954；危險：#D70015；中性／離線：#86868B
   用色克制，只做小圓點與文字，不做大面積填色
   ---------------------------------------------------------------------------- */

export type StatusVariant = 'online' | 'danger' | 'neutral'

export interface StatusBadgeProps {
  variant?: StatusVariant
  label: string
  dot?: boolean
}

export function StatusBadge({ variant = 'online', label, dot = true }: StatusBadgeProps) {
  const dotColor =
    variant === 'online'
      ? t.color.success
      : variant === 'danger'
        ? t.color.danger
        : t.color.textSecondary
  const textColor = variant === 'danger' ? t.color.danger : t.color.textSecondary

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: t.font.family,
        fontSize: 12.5,
        fontWeight: 500,
        color: textColor,
      }}
    >
      {dot && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: t.radius.pill,
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </span>
  )
}

/* ----------------------------------------------------------------------------
   內建 chevron 圖示（文字連結按鈕使用）
   ---------------------------------------------------------------------------- */

export function ChevronRightIcon({
  size = 14,
  color = t.color.accent,
}: {
  size?: number
  color?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9 6l6 6-6 6"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
